import React, {
  cloneElement,
  Ref,
  ReactElement,
  useRef,
  useImperativeHandle,
  RefObject,
} from 'react';

import { Popover, PopoverProps } from '@mui/material';
import { useId } from '@mantine/hooks';
import { useOpen } from '../../../hooks';

export interface PopoverInstanceType {
  close: () => void;
  open: () => void;
}

interface IPopoverProps {
  trigger: ReactElement<{ onClick: () => void; ref: RefObject<null> }>;
  popoverProps?: Omit<PopoverProps, 'open' | 'onClose' | 'anchorEl'>;
  children: React.ReactNode;
  actionBeforeClose?: (handleClose: () => void) => void;
  ref?: Ref<PopoverInstanceType>;
}

const CustomPopover = ({
  ref,
  trigger,
  children,
  popoverProps,
  actionBeforeClose,
}: IPopoverProps) => {
  const id = useId();

  const [isOpen, open, close] = useOpen();
  const triggerElRef = useRef(null);

  const triggerEl = cloneElement(trigger, {
    onClick: open,
    ref: triggerElRef,
  });

  useImperativeHandle(ref, () => ({ close, open }), []);

  const handleClose = () => {
    if (actionBeforeClose) {
      actionBeforeClose(close);
    } else {
      close();
    }
  };

  return (
    <>
      <Popover
        id={id}
        open={isOpen}
        onClose={handleClose}
        anchorEl={triggerElRef.current}
        {...popoverProps}
      >
        {children}
      </Popover>
      {triggerEl}
    </>
  );
};

export default CustomPopover;
